import PublicLayout from "@/components/PublicLayout";
import { useState } from "react";
import { Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    toast.success("Thank you — your message has been received.", {
      description: "We read every note prayerfully.",
    });
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <PublicLayout>
      <section className="celestial-bg">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20">
          <p className="text-xs uppercase tracking-widest text-brand-quiet">Contact</p>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl tracking-tight text-brand-ink">
            We’d love to hear from you.
          </h1>
          <p className="mt-4 text-lg text-brand-quiet max-w-xl leading-relaxed">
            Questions, prayer requests, or feedback — write to us. Every message matters.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-2 space-y-6">
          <Item icon={Mail} label="Email" value="noreply@pathtorepentance.com" />
          <Item icon={MessageCircle} label="Community" value="A safe, anonymous space" />
          <p className="scripture-quote text-2xl text-brand-ink leading-snug pt-4">
            “Cast your burden on the Lord, and He shall sustain you.”
          </p>
          <p className="text-xs text-brand-muted">— Psalm 55:22</p>
        </div>
        <form
          onSubmit={submit}
          className="md:col-span-3 rounded-2xl bg-white border border-brand-line p-7 space-y-4"
          data-testid="contact-form"
        >
          <Field
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            testid="contact-name-input"
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            testid="contact-email-input"
          />
          <div>
            <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">
              Message
            </label>
            <textarea
              required
              rows={5}
              data-testid="contact-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
            />
          </div>
          <button
            type="submit"
            data-testid="contact-submit-button"
            className="inline-flex items-center gap-2 rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-6 py-3 font-medium"
          >
            Send message <Send className="w-4 h-4" strokeWidth={1.7} />
          </button>
        </form>
      </section>
    </PublicLayout>
  );
}

function Item({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <span className="w-10 h-10 rounded-full bg-brand-mist text-brand-skyDark grid place-items-center">
        <Icon className="w-4 h-4" strokeWidth={1.7} />
      </span>
      <div>
        <p className="text-xs uppercase tracking-wider text-brand-quiet">{label}</p>
        <p className="text-brand-ink">{value}</p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">{label}</label>
      <input
        required
        type={type}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
      />
    </div>
  );
}
